import { Alert, Button } from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { OutlinedCommentsIcon } from "@patternfly/react-icons";
import { ErrorPopup, SuccessPopup, useAlertContext } from "@rhc-shared-components/alert-component";
import type {
	Env,
	PartnerViewConfig,
	QuestionnaireHandle,
	ValidationStatus,
} from "@rhc-shared-components/questionnaire";
import { Questionnaire } from "@rhc-shared-components/questionnaire";
import { type FC, useRef, useState } from "react";
import EnvUtil from "../../../utils/env";
import { getAuthToken } from "../../../utils/fetch";

export interface FeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
	generationId: string;
}

const QUESTIONNAIRE_ALIAS = "ai_pls_feedback";

const getQuestionnaireEnv = (): Env => {
	const name = EnvUtil.getEnvName();
	if (name === "PROD" || name === "UAT") return "PROD";
	if (name === "STAGE") return "STAGE";
	return "QA";
};

const FeedbackModal: FC<FeedbackModalProps> = ({ isOpen, onClose, generationId }) => {
	const questionnaireRef = useRef<QuestionnaireHandle>(null);
	const [formStatus, setFormStatus] = useState<ValidationStatus>("Invalid");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { setAlertState } = useAlertContext();

	const handleSubmit = async () => {
		if (!questionnaireRef.current) return;
		setIsSubmitting(true);
		try {
			await questionnaireRef.current.submit();
			SuccessPopup(setAlertState, "Feedback submitted");
			onClose();
		} catch {
			ErrorPopup(setAlertState, "Failed to send feedback");
		} finally {
			setIsSubmitting(false);
		}
	};

	const modalHeader = (
		<div className="ai-import-modal__header">
			<OutlinedCommentsIcon />
			<span>Feedback for AI-generated product fields</span>
		</div>
	);

	return (
		<Modal
			variant={ModalVariant.medium}
			isOpen={isOpen}
			onClose={onClose}
			appendTo={document.getElementById("modal-div-container") ?? document.body}
			id="ai-feedback-modal"
			className="ai-import-modal"
			aria-label="Feedback for AI-generated product fields"
			header={modalHeader}
			actions={[
				<Button
					key="send"
					variant="primary"
					isLoading={isSubmitting}
					isDisabled={isSubmitting || formStatus === "Invalid"}
					onClick={handleSubmit}
				>
					Send
				</Button>,
				<Button key="cancel" variant="link" onClick={onClose} isDisabled={isSubmitting}>
					Cancel
				</Button>,
			]}
		>
			<Alert
				variant="info"
				isInline
				title="Do not include any personal information or other sensitive information in your feedback. Feedback may be used to improve Red Hat's products or services."
			/>
			<Questionnaire
				ref={questionnaireRef}
				config={
					{
						mode: "partner",
						alias: QUESTIONNAIRE_ALIAS,
						uniqueEntityId: generationId,
						env: getQuestionnaireEnv(),
						showBuiltInSubmitButton: false,
						hideSubmitError: true,
						setStatus: setFormStatus,
						getAuthToken,
					} as PartnerViewConfig
				}
			/>
		</Modal>
	);
};

export default FeedbackModal;
